'use client';

import Link from 'next/link';
import { AppShell } from '@/components/AppShell';

function TermsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-h3 font-serif font-semibold text-text-primary">{title}</h2>
      {children}
    </section>
  );
}

export default function TermsPage() {
  return (
    <AppShell showPlayer={false}>
      <article className="max-w-4xl mx-auto space-y-10 min-w-0 w-full pb-12">
        <header className="space-y-2 border-b border-border pb-8">
          <h1 className="text-h1 font-serif font-bold text-text-primary">Terms of Service</h1>
          <p className="text-body-sm text-text-secondary">Last updated: July 7, 2026</p>
        </header>

        <TermsSection title="1. Acceptance">
          <p className="text-body text-text-secondary leading-relaxed">
            By accessing or using Astradio at astradio.io or the Astradio mobile application, you agree to these
            terms. If you do not agree, do not use the service.
          </p>
        </TermsSection>

        <TermsSection title="2. What Astradio Does">
          <p className="text-body text-text-secondary leading-relaxed">
            Astradio composes personalized astrological readings and audio compositions from natal chart data you
            provide. Readings and audio are for entertainment and self-reflection. They are not professional advice of
            any kind.
          </p>
        </TermsSection>

        <TermsSection title="3. Your Account">
          <p className="text-body text-text-secondary leading-relaxed">
            You must provide accurate birth data to use chart features. You are responsible for keeping your login
            credentials secure. You may delete your account at any time through the app settings, which removes your
            data from our systems.
          </p>
        </TermsSection>

        <TermsSection title="4. Content You Create">
          <p className="text-body text-text-secondary leading-relaxed">
            You retain ownership of any content you post (profile bios, community posts, audio labels). By posting to
            Astradio&apos;s community features, you grant us a license to display that content within the platform. We
            do not sell or sublicense your content to third parties.
          </p>
        </TermsSection>

        <TermsSection title="5. Content We Create">
          <p className="text-body text-text-secondary leading-relaxed">
            Readings, transit summaries, compatibility reports, and audio compositions generated by Astradio are produced
            for your personal use. You may share them, but you may not resell or redistribute them commercially.
          </p>
        </TermsSection>

        <TermsSection title="6. Acceptable Use">
          <p className="text-body text-text-secondary leading-relaxed">
            Do not use Astradio to harass, impersonate, or harm others. Do not upload offensive or illegal content. We
            reserve the right to remove content or suspend accounts that violate these terms.
          </p>
        </TermsSection>

        <TermsSection title="7. Audio Compositions">
          <p className="text-body text-text-secondary leading-relaxed">
            Audio is composed using AI-assisted generation. Compositions are unique to your chart data but are not
            guaranteed to be musically distinct from compositions generated for other users. Audio files are stored on
            our servers and available as long as your account is active.
          </p>
        </TermsSection>

        <TermsSection title="8. Availability">
          <p className="text-body text-text-secondary leading-relaxed">
            Astradio is provided as-is. We do not guarantee uninterrupted availability. We may modify or discontinue
            features at any time.
          </p>
        </TermsSection>

        <TermsSection title="9. Limitation of Liability">
          <p className="text-body text-text-secondary leading-relaxed">
            Astradio is not liable for decisions you make based on readings or compositions. Our total liability for
            any claim related to the service is limited to the amount you have paid us in the twelve months preceding
            the claim, if any.
          </p>
        </TermsSection>

        <TermsSection title="10. Changes">
          <p className="text-body text-text-secondary leading-relaxed">
            We may update these terms. Continued use after changes constitutes acceptance. Material changes will be
            communicated through the app or by email.
          </p>
        </TermsSection>

        <TermsSection title="11. Contact">
          <p className="text-body text-text-secondary leading-relaxed">
            Questions about these terms can be directed to{' '}
            <a href="mailto:support@astradio.io" className="text-accent hover:underline">
              support@astradio.io
            </a>
            .
          </p>
        </TermsSection>

        <p className="text-body-sm text-text-muted text-center pt-4 border-t border-border">
          <Link href="/settings" className="text-accent hover:underline">
            Settings
          </Link>
          {' · '}
          <Link href="/today" className="text-accent hover:underline">
            Today
          </Link>
          {' · '}
          <Link href="/privacy" className="text-accent hover:underline">
            Privacy Policy
          </Link>
          {' · '}
          <Link href="/terms" className="text-accent hover:underline">
            Terms of Service
          </Link>
        </p>
      </article>
    </AppShell>
  );
}
